import { get } from '@ember/object';
import changesetGet from 'ember-changeset/helpers/changeset-get';
import ScaleSurveyEditionFormComponent from 'parog-web/components/abstract/scale-survey-edition-form';
import Provider from 'parog-web/components/forc/provider';
import YmrsDeclaredJournalEvent from 'parog-web/models/ymrs-declared-journal-event';
import ymrsScoreDescription from 'parog-web/utils/ymrs-score-description';
import TwoButtonTabs from 'ui-kit/components/two-button-tabs';

export default class YMRSEditionComponent extends ScaleSurveyEditionFormComponent {
  static override forcDisplayName = 'Young Mania Rating Scale';
  static override fullComponentId = 'scale-surveys/ymrs-edition-form';
  static override journalEventClass = YmrsDeclaredJournalEvent;

  override get maximalScore() {
    return 60;
  }

  override get numberOfItems() {
    return 11;
  }

  override get validationNames() {
    return [
      "L'élévation de l'humeur",
      "L'activité motrice et énergie augmentées",
      "L'intérêt sexuel",
      'Le sommeil',
      "L'irritabilité",
      'Le discours (débit et quantité)',
      'Le langage – troubles de la pensée',
      'Le contenu',
      'Le comportement agressif et perturbateur',
      "L'apparence",
      "L'introspection"
    ];
  }

  // payload will either be a changeset (when template refreshes),
  // or a POJO (when the form is saved).
  override _computeScore(payload) {
    const number = this.questionIds.reduce(
      (total, index) => total + parseInt(get(payload, index)),
      0
    );

    return {
      number,
      description: ymrsScoreDescription(number)
    };
  }

  <template>
    <Provider @forcComponent={{this}}>
      <:content as |Form|>
        <TwoButtonTabs
          class='large'
          @selected={{if
            (changesetGet this.changeset 'isTotalScoreFilled')
            'second'
            'first'
          }}
          @onClick={{this.onTabButtonClicked}}
        >
          <:firstButton>Répondre aux questions</:firstButton>
          <:secondButton>Renseigner le score</:secondButton>

          <:firstTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>
            <div class='field'>
              <Form.Dropdown
                @labelName="Élévation de l'humeur"
                @propertyName='0'
                as |dropdown|
              >
                <dropdown.Item @value='Absente' @key='0' />
                <dropdown.Item
                  @value='Légèrement ou possiblement élevée lorsqu’on l’interroge'
                  @key='1'
                />
                <dropdown.Item
                  @value='Élévation subjective nette ; optimiste, pleine d’assurance ; gaie ; contenu approprié'
                  @key='2'
                />
                <dropdown.Item
                  @value='Élevée, au contenu inapproprié ; plaisantine'
                  @key='3'
                />
                <dropdown.Item
                  @value='Euphorique ; rires inappropriés ; chante'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Activité motrice et énergie augmentées'
                @propertyName='1'
                as |dropdown|
              >
                <dropdown.Item @value='Absente' @key='0' />
                <dropdown.Item @value='Subjectivement élevées' @key='1' />
                <dropdown.Item
                  @value='Animée ; expression gestuelle plus élevée'
                  @key='2'
                />
                <dropdown.Item
                  @value='Énergie excessive ; parfois hyperactif ; agité (peut être calmé)'
                  @key='3'
                />
                <dropdown.Item
                  @value='Excitation motrice ; hyperactivité continuelle (ne peut être calmé)'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Intérêt sexuel'
                @propertyName='2'
                as |dropdown|
              >
                <dropdown.Item @value='Normal ; non augmenté' @key='0' />
                <dropdown.Item
                  @value='Augmentation légère ou possible'
                  @key='1'
                />
                <dropdown.Item
                  @value='Clairement augmenté lorsqu’on l’interroge'
                  @key='2'
                />
                <dropdown.Item
                  @value='Parle spontanément de la sexualité ; élabore sur des thèmes sexuels ; se décrit comme étant hyper sexuel'
                  @key='3'
                />
                <dropdown.Item
                  @value='Agissements sexuels manifestes (envers les patients, les membres de l’équipe, ou l’évaluateur)'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Sommeil'
                @propertyName='3'
                as |dropdown|
              >
                <dropdown.Item
                  @value='Ne rapporte pas de diminution de sommeil'
                  @key='0'
                />
                <dropdown.Item
                  @value='Dort jusqu’à une heure de moins que d’habitude'
                  @key='1'
                />
                <dropdown.Item
                  @value='Sommeil réduit de plus d’une heure par rapport à l’habitude'
                  @key='2'
                />
                <dropdown.Item
                  @value='Rapporte un moins grand besoin de sommeil'
                  @key='3'
                />
                <dropdown.Item @value='Nie le besoin de sommeil' @key='4' />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Irritabilité'
                @propertyName='4'
                as |dropdown|
              >
                <dropdown.Item @value='Absente' @key='0' />
                <dropdown.Item @value='Subjectivement augmentée' @key='2' />
                <dropdown.Item
                  @value='Irritable par moment durant l’entretien ; épisodes récents d’énervement ou de colère dans le service'
                  @key='4'
                />
                <dropdown.Item
                  @value='Fréquemment irritable durant l’entretien ; brusque ; abrupt'
                  @key='6'
                />
                <dropdown.Item
                  @value='Hostile, non coopératif ; évaluation impossible'
                  @key='8'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Discours (débit et quantité)'
                @propertyName='5'
                as |dropdown|
              >
                <dropdown.Item @value='Pas augmenté' @key='0' />
                <dropdown.Item @value='Se sent bavard' @key='2' />
                <dropdown.Item
                  @value='Augmentation du débit ou de la quantité par moment ; prolixe par moment'
                  @key='4'
                />
                <dropdown.Item
                  @value='Soutenu ; augmentation consistante du débit ou de la quantité ; difficile à interrompre'
                  @key='6'
                />
                <dropdown.Item
                  @value='Sous pression ; impossible à interrompre ; discours continu'
                  @key='8'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Langage – troubles de la pensée'
                @propertyName='6'
                as |dropdown|
              >
                <dropdown.Item @value='Absent' @key='0' />
                <dropdown.Item
                  @value='Circonstanciel ; légère distractivité ; pensées rapides'
                  @key='1'
                />
                <dropdown.Item
                  @value='Distractivité ; perd le fil de ses idées ; change fréquemment de sujet ; pensées accélérées'
                  @key='2'
                />
                <dropdown.Item
                  @value='Fuite des idées ; réponses hors sujet ; difficile à suivre ; fait des rimes, écholalie'
                  @key='3'
                />
                <dropdown.Item
                  @value='Incohérent ; communication impossible'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Contenu'
                @propertyName='7'
                as |dropdown|
              >
                <dropdown.Item @value='Normal' @key='0' />
                <dropdown.Item
                  @value='Projets discutables ; intérêts nouveaux'
                  @key='2'
                />
                <dropdown.Item
                  @value='Projet(s) particulier(s) ; hyper religieux'
                  @key='4'
                />
                <dropdown.Item
                  @value='Idées de grandeur ou de persécution ; idées de référence'
                  @key='6'
                />
                <dropdown.Item @value='Délires ; hallucinations' @key='8' />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Comportement agressif et perturbateur'
                @propertyName='8'
                as |dropdown|
              >
                <dropdown.Item @value='Absent, coopératif' @key='0' />
                <dropdown.Item
                  @value='Sarcastique ; parle fort par moment, sur la défensive'
                  @key='2'
                />
                <dropdown.Item
                  @value='Exigeant ; fait des menaces dans le service'
                  @key='4'
                />
                <dropdown.Item
                  @value='Menace l’évaluateur ; crie ; évaluation difficile'
                  @key='6'
                />
                <dropdown.Item
                  @value='Agressif physiquement ; destructeur ; évaluation impossible'
                  @key='8'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Apparence'
                @propertyName='9'
                as |dropdown|
              >
                <dropdown.Item
                  @value='Soignée et habillement adéquat'
                  @key='0'
                />
                <dropdown.Item @value='Légèrement négligée' @key='1' />
                <dropdown.Item
                  @value='Peu soignée ; modérément débraillée ; trop habillée'
                  @key='2'
                />
                <dropdown.Item
                  @value='Débraillée ; à moitié nue ; maquillage criard'
                  @key='3'
                />
                <dropdown.Item
                  @value='Complètement négligée ; ornée ; accoutrement bizarre'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='field'>
              <Form.Dropdown
                @labelName='Introspection'
                @propertyName='10'
                as |dropdown|
              >
                <dropdown.Item
                  @value='Présente ; admet être malade ; reconnaît le besoin de traitement'
                  @key='0'
                />
                <dropdown.Item @value='Éventuellement malade' @key='1' />
                <dropdown.Item
                  @value='Admet des changements de comportement, mais nie la maladie'
                  @key='2'
                />
                <dropdown.Item
                  @value='Admet de possibles changements de comportement, mais nie la maladie'
                  @key='3'
                />
                <dropdown.Item
                  @value='Nie tout changement de comportement'
                  @key='4'
                />
              </Form.Dropdown>
            </div>

            <div class='ui message' data-test-score>
              <strong>Score : </strong>
              {{#if this.isChangesetValid}}
                {{this.score.number}}
                –
                {{this.score.description}}
              {{else}}
                veuillez remplir tous les champs afin d'obtenir le score.
              {{/if}}
            </div>
          </:firstTab>

          <:secondTab>
            <div class='field'>
              <Form.Date
                @labelName='Date à laquelle le questionnaire a été renseigné'
                @propertyName='occurredAt'
              />
            </div>
            <div class='field'>
              <Form.NumberInput
                @labelName='Score total'
                @propertyName='total'
                @type='integer'
              />
            </div>
          </:secondTab>
        </TwoButtonTabs>
      </:content>
    </Provider>
  </template>
}
